import { useState, useEffect } from 'react';
import { FormData, Formula } from '../types';
import { INITIAL_FORM_DATA } from '../constants';
import { submitOfferte } from '../services/api';
import { resolveFormulaSlug } from '../../../hooks/useFormulas';
import { openMinutes } from '../../../lib/formRules';

export type ValidationErrors = Record<string, string>;

const STORAGE_KEY = 'offerte_form_data';

// The steps of the request, each with the fields it checks before moving on
export const STEPS = [
  { key: 'formule', label: 'Formule', fields: ['formula'] },
  { key: 'event', label: 'Je event', fields: ['volwassenen', 'kinderen', 'datum', 'startTijd', 'eindTijd', 'andereFoodtrucks'] },
  { key: 'locatie', label: 'Locatie', fields: ['eventLocatieNaam', 'eventStraat', 'eventHuisnummer', 'eventPostcode', 'eventGemeente'] },
  { key: 'contact', label: 'Contact', fields: ['eventVoornaam', 'eventNaam', 'eventEmail', 'eventPhoneNumber', 'factuurStraat', 'factuurHuisnummer', 'factuurPostcode', 'factuurGemeente'] },
  { key: 'overzicht', label: 'Overzicht', fields: [] },
] as const;

const todayIso = () => new Date().toISOString().slice(0, 10);

const collectErrors = (formData: FormData, minimumPersons: number): ValidationErrors => {
  const errors: ValidationErrors = {};
  const require = (field: keyof FormData, message: string) => {
    if (!formData[field]) errors[field] = message;
  };

  require('formula', 'Kies een formule');

  const totalPersons = (formData.volwassenen || 0) + (formData.kinderen || 0);
  if (!formData.volwassenen) {
    errors.volwassenen = 'Vul het aantal volwassenen in';
  } else if (totalPersons < minimumPersons) {
    errors.volwassenen = `We komen vanaf ${minimumPersons} gasten (volwassenen en kinderen samen). Nu: ${totalPersons}.`;
  }
  if (!formData.datum) {
    errors.datum = 'Kies de datum van je event';
  } else if (formData.datum < todayIso()) {
    errors.datum = 'Deze datum ligt in het verleden';
  }
  require('startTijd', 'Kies een startuur');
  require('eindTijd', 'Kies een einduur');
  if (formData.startTijd && formData.eindTijd && openMinutes(formData.startTijd, formData.eindTijd) === null) {
    errors.eindTijd = 'Het einduur moet na het startuur liggen';
  }
  if (formData.andereFoodtrucks === null || formData.andereFoodtrucks === undefined) {
    errors.andereFoodtrucks = 'Laat weten of er nog andere foodtrucks of cateraars zijn';
  }

  require('eventLocatieNaam', 'Geef de locatie een naam, bv. "Thuis" of de naam van de zaal');
  require('eventStraat', 'Vul de straat in');
  require('eventHuisnummer', 'Vul het huisnummer in');
  require('eventPostcode', 'Vul de postcode in');
  require('eventGemeente', 'Vul de gemeente in');

  require('eventVoornaam', 'Vul je voornaam in');
  require('eventNaam', 'Vul je achternaam in');
  if (!formData.eventEmail) {
    errors.eventEmail = 'Vul je e-mailadres in';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.eventEmail)) {
    errors.eventEmail = 'Dit e-mailadres klopt niet helemaal';
  }
  require('eventPhoneNumber', 'Vul je telefoonnummer in');

  if (!formData.sameAsEvent) {
    require('factuurStraat', 'Vul de straat in');
    require('factuurHuisnummer', 'Vul het huisnummer in');
    require('factuurPostcode', 'Vul de postcode in');
    require('factuurGemeente', 'Vul de gemeente in');
  }

  return errors;
};

const scrollToField = (field: string) => {
  const element = document.querySelector<HTMLElement>(`[name="${field}"]`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus({ preventScroll: true });
  }
};

export const useOfferteForm = (formulas: Formula[], requestedFormula: string | null | undefined, minimumPersons: number) => {
  const [formData, setFormData] = useState<FormData>(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        // Merge so fields added after the user last saved still get defaults
        return { ...INITIAL_FORM_DATA, ...JSON.parse(savedData) };
      } catch {
        return INITIAL_FORM_DATA;
      }
    }
    return INITIAL_FORM_DATA;
  });

  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  // What was sent, kept for the confirmation screen after the form is reset
  const [submitted, setSubmitted] = useState<FormData | null>(null);

  // Once the formulas are loaded, make sure the selected one exists: a formula picked on the
  // homepage (?formule=) wins, stale saved values (optie1/optie2) map to their new formula
  useEffect(() => {
    if (formulas.length === 0) return;
    setFormData((prev) => {
      const formula = resolveFormulaSlug(requestedFormula || prev.formula, formulas);
      return formula === prev.formula ? prev : { ...prev, formula };
    });
  }, [formulas, requestedFormula]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const clearError = (field: string) => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Checks the current step; moves on when it's complete, otherwise points at the first problem
  const goNext = () => {
    const allErrors = collectErrors(formData, minimumPersons);
    const stepFields: readonly string[] = STEPS[step].fields;
    const stepErrors = Object.fromEntries(Object.entries(allErrors).filter(([field]) => stepFields.includes(field)));
    setValidationErrors(stepErrors);
    const firstError = stepFields.find((field) => stepErrors[field]);
    if (firstError) {
      scrollToField(firstError);
      return;
    }
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setValidationErrors({});
    setStep((current) => Math.max(current - 1, 0));
  };

  // Jump back to an earlier step, e.g. from the overview
  const goToStep = (index: number) => {
    if (index < step) {
      setValidationErrors({});
      setStep(index);
    }
  };

  const submit = async () => {
    const errors = collectErrors(formData, minimumPersons);
    if (Object.keys(errors).length > 0) {
      // Send the customer to the first step that still needs attention
      const index = STEPS.findIndex((item) => (item.fields as readonly string[]).some((field) => errors[field]));
      setValidationErrors(errors);
      setStep(Math.max(index, 0));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await submitOfferte(formData);
      if (response.success) {
        localStorage.removeItem(STORAGE_KEY);
        setSubmitted(formData);
        setFormData({ ...INITIAL_FORM_DATA, formula: resolveFormulaSlug(null, formulas) });
        setValidationErrors({});
        setStep(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError(response.message || 'Je aanvraag kon niet verstuurd worden. Probeer het opnieuw.');
      }
    } catch {
      setSubmitError('Je aanvraag kon niet verstuurd worden. Controleer je internetverbinding en probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSameAsEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      sameAsEvent: isChecked,
      factuurStraat: isChecked ? prev.eventStraat : '',
      factuurHuisnummer: isChecked ? prev.eventHuisnummer : '',
      factuurPostcode: isChecked ? prev.eventPostcode : '',
      factuurGemeente: isChecked ? prev.eventGemeente : '',
    }));
    if (isChecked) {
      ['factuurStraat', 'factuurHuisnummer', 'factuurPostcode', 'factuurGemeente'].forEach(clearError);
    }
  };

  const handleEventAddressChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(prev.sameAsEvent && field.startsWith('event') && {
        [`factuur${field.slice(5)}`]: value,
      }),
    }));
    clearError(field);
  };

  const updateFormData = (field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
    if (field === 'kinderen') clearError('volwassenen');
  };

  return {
    formData,
    step,
    isSubmitting,
    submitError,
    submitted,
    validationErrors,
    goNext,
    goBack,
    goToStep,
    submit,
    startOver: () => setSubmitted(null),
    handleSameAsEventChange,
    handleEventAddressChange,
    updateFormData,
  };
};
